import React, { useState, useEffect } from 'react';
import {
  BookOpen,
  X,
  Info,
  Upload,
  FolderOpen,
  Settings,
  Clock,
  Users,
  Trash2,
} from 'lucide-react';
import '../CSS/GuideModal.css';

const LearningMaterialsGuideModal = () => {
  const [isOpen, setIsOpen] = useState(false);

  // Prevent body scroll when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') setIsOpen(false); };
    if (isOpen) document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen]);

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) setIsOpen(false);
  };

  return (
    <>
      {/* ── Floating Action Button ── */}
      <div className="gml-fab-wrapper">
        <span className="gml-fab-tooltip">Need help?</span>
        <button
          className="gml-fab"
          onClick={() => setIsOpen(true)}
          aria-label="Open Learning Materials Guide"
          title="Learning Materials Guide"
        >
          <BookOpen className="gml-fab-icon" />
        </button>
      </div>

      {/* ── Modal ── */}
      {isOpen && (
        <div
          className="gml-backdrop"
          onClick={handleBackdropClick}
          role="dialog"
          aria-modal="true"
          aria-labelledby="gml-lm-modal-title"
        >
          <div className="gml-modal">

            {/* Header */}
            <div className="gml-modal-header">
              <div className="gml-modal-title-wrap">
                <BookOpen className="gml-modal-header-icon" />
                <h2 className="gml-modal-title" id="gml-lm-modal-title">
                  Learning Materials Module Guide
                </h2>
              </div>
              <button
                className="gml-modal-close"
                onClick={() => setIsOpen(false)}
                aria-label="Close guide"
              >
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="gml-modal-body">

              {/* Welcome Banner */}
              <div className="gml-welcome-banner">
                <Info className="gml-welcome-icon" />
                <div>
                  <p className="gml-welcome-heading">
                    Welcome to the Learning Materials Module!
                  </p>
                  <p className="gml-welcome-text">
                    This guide will help you manage and access educational
                    materials for your classes. Use it anytime you need a
                    quick reference.
                  </p>
                </div>
              </div>

              {/* ── Key Features Overview ── */}
              <h3 className="gml-section-heading">Key Features Overview</h3>
              <div className="gml-cards-grid">

                {/* Materials Upload */}
                <div className="gml-feature-card gml-feature-card--blue">
                  <div className="gml-card-head gml-card-head--blue">
                    <Upload className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Materials Upload</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">1</span>
                        Upload files for specific classes
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">2</span>
                        Add descriptive information
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--blue">3</span>
                        Track upload progress
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Materials Access */}
                <div className="gml-feature-card gml-feature-card--green">
                  <div className="gml-card-head gml-card-head--green">
                    <FolderOpen className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Materials Access</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">1</span>
                        View materials by class
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">2</span>
                        Download files with one click
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--green">3</span>
                        See upload dates and descriptions
                      </li>
                    </ul>
                  </div>
                </div>

                {/* Materials Management */}
                <div className="gml-feature-card gml-feature-card--yellow">
                  <div className="gml-card-head gml-card-head--yellow">
                    <Settings className="gml-card-head-icon" />
                    <h4 className="gml-card-head-title">Materials Management</h4>
                  </div>
                  <div className="gml-card-body">
                    <ul className="gml-card-list">
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--yellow">1</span>
                        Delete outdated materials
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--yellow">2</span>
                        Organize by class
                      </li>
                      <li className="gml-card-list-item">
                        <span className="gml-card-badge gml-card-badge--yellow">3</span>
                        Role-based access control
                      </li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Detailed Feature Guide ── */}
              <h3 className="gml-section-heading">Detailed Feature Guide</h3>
              <div className="gml-detail-section">

                {/* Uploading Materials */}
                <div className="gml-detail-card gml-detail-card--cyan">
                  <div className="gml-detail-head gml-detail-head--cyan">
                    <Clock className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Uploading Materials</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--cyan">Basic Workflow</p>
                    <ul className="gml-detail-list">
                      <li><strong>Select Class:</strong> Choose the target class from the dropdown</li>
                      <li><strong>Enter Description:</strong> Add a clear, concise description</li>
                      <li><strong>Choose File:</strong> Select the file you want to upload</li>
                      <li><strong>Upload:</strong> Click "Upload Material" to submit</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--cyan">File Requirements</p>
                    <ul className="gml-detail-list">
                      <li>Supported formats: PDF, DOCX, PPTX, XLSX, JPG, PNG</li>
                      <li>Maximum file size: 25MB per upload</li>
                      <li>Descriptions should be clear and concise</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--cyan">Visual Indicators</p>
                    <ul className="gml-detail-list">
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--success">Uploading...</span>
                        — Progress bar shown during upload
                      </li>
                      <li>
                        <span className="gml-inline-badge gml-inline-badge--muted">Error</span>
                        — Notification shown if upload fails
                      </li>
                      <li>Success toast displayed when upload completes</li>
                    </ul>
                  </div>
                </div>

                {/* Accessing Materials */}
                <div className="gml-detail-card gml-detail-card--yellow">
                  <div className="gml-detail-head gml-detail-head--yellow">
                    <Users className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Accessing Materials</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--yellow">For Students</p>
                    <ul className="gml-detail-list">
                      <li>Only see materials assigned to your class</li>
                      <li>Download files with a single click</li>
                      <li>View descriptions and upload dates for context</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">For Teachers / Admins</p>
                    <ul className="gml-detail-list">
                      <li>View materials across all classes</li>
                      <li>Materials organized by class in expandable accordions</li>
                      <li>See file counts per class at a glance</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--yellow">Downloading</p>
                    <ul className="gml-detail-list">
                      <li>Files download with their original filenames</li>
                      <li>Progress indicator shown during download</li>
                      <li>Success notification displayed when complete</li>
                    </ul>
                  </div>
                </div>

                {/* Managing Materials */}
                <div className="gml-detail-card gml-detail-card--green">
                  <div className="gml-detail-head gml-detail-head--green">
                    <Trash2 className="gml-detail-head-icon" />
                    <h5 className="gml-detail-title">Managing Materials</h5>
                  </div>
                  <div className="gml-detail-body">
                    <p className="gml-detail-sub gml-detail-sub--green">Admin Privileges</p>
                    <ul className="gml-detail-list">
                      <li>Delete outdated or incorrect materials</li>
                      <li>Reorganize materials across classes as needed</li>
                      <li>Update class assignments for existing files</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--green">Staff Access</p>
                    <ul className="gml-detail-list">
                      <li>View-only access for staff members</li>
                      <li>Can download any available material</li>
                      <li>Cannot modify or delete existing files</li>
                    </ul>

                    <p className="gml-detail-sub gml-detail-sub--green">Usage Tips</p>
                    <ul className="gml-detail-list">
                      <li>Use clear, descriptive filenames before uploading</li>
                      <li>Include detailed descriptions for student clarity</li>
                      <li>Regularly review and clean up outdated materials</li>
                    </ul>
                  </div>
                </div>

              </div>

              {/* ── Best Practices ── */}
              <div className="gml-best-practices">
                <p className="gml-best-practices-heading">Best Practices</p>
                <ul className="gml-best-practices-list">
                  {[
                    'Keep file sizes reasonable to ensure fast, easy downloading.',
                    'Use standard formats (PDF, DOCX) that work on all devices.',
                    'Organize materials by topic or unit in the description field.',
                    'Regularly review and replace outdated materials.',
                    'Use clear, consistent naming conventions across all uploads.',
                    'Access this guide anytime via the floating button at the bottom-right of the screen.',
                  ].map((tip, i) => (
                    <li key={i}>
                      <span className="gml-bp-dot" aria-hidden="true" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </div>

            </div>

            {/* Footer */}
            <div className="gml-modal-footer">
              <button className="gml-close-btn" onClick={() => setIsOpen(false)}>
                Got it
              </button>
            </div>

          </div>
        </div>
      )}
    </>
  );
};

export default LearningMaterialsGuideModal;